// ─────────────────────────────────────────────────────────────────────────
// Long-form text pages (Shipping & Returns, Privacy, Terms).
// Returns copy mirrors the live policy at ecoffeecup.co.za/returns —
// keep the two in step if the client updates the policy.
// ─────────────────────────────────────────────────────────────────────────

export type TextBlock =
  | { type: 'p'; text: string }
  | { type: 'h3'; text: string }
  | { type: 'list'; items: string[] }
  | { type: 'note'; text: string }
  | { type: 'table'; head: string[]; rows: string[][] }

export type TextSection = {
  id: string
  title: string
  blocks: TextBlock[]
}

export const shippingReturnsSections: TextSection[] = [
  {
    id: 'introduction',
    title: 'Introduction',
    blocks: [
      {
        type: 'p',
        text: 'Ecoffee Cup® South Africa does not allow returns once a product has been purchased, unless there is clear evidence that the product has a factory defect. Products purchased on the Ecoffee Cup® South Africa website are subject to a 7-day return/cooling-off policy, subject to the terms and conditions below.',
      },
      {
        type: 'p',
        text: 'This right of return only applies to products returned in their original, complete, unused condition. Any product that has been damaged, is not in its original packaging, or has packaging that has been worn beyond simply opening the product, will not be refunded. Please address returns to the address provided below.',
      },
      { type: 'h3', text: 'Web orders and enquiries' },
      {
        type: 'note',
        text: 'For all enquiries relating to orders placed through this website ONLY, please email sales@ecoffeecup.co.za. Please quote your order number in the subject line of all correspondence.',
      },
    ],
  },
  {
    id: 'returns',
    title: 'Returns',
    blocks: [
      {
        type: 'p',
        text: 'At Ecoffee Cup® South Africa, we are committed to providing high-quality products and excellent customer service. This Refunds and Returns Policy explains your rights and our procedures regarding returns, exchanges, repairs, and refunds.',
      },
      { type: 'h3', text: 'Consumer rights' },
      {
        type: 'p',
        text: 'These return and replacement terms and conditions are subject to the Consumer Protection Act, 68 of 2008 (“CPA”), and any other applicable South African consumer protection laws.',
      },
      { type: 'h3', text: 'Defective, damaged or faulty products' },
      {
        type: 'p',
        text: 'If your product is defective, damaged on delivery, or develops a fault that is covered under the CPA, you may be entitled to a repair, replacement, or refund. Please notify us as soon as reasonably possible after discovering the defect by emailing sales@ecoffeecup.co.za.',
      },
    ],
  },
  {
    id: 'exchanges-returns',
    title: 'Exchanges or returns',
    blocks: [
      {
        type: 'p',
        text: 'Customers must initiate any request for a product return with the retailer where the item was purchased. Ecoffee Cup® South Africa will only process exchanges or returns for products bought directly from our website or office.',
      },
      { type: 'h3', text: 'Timeframe' },
      {
        type: 'p',
        text: 'Customers must initiate any requests for product returns within 7 days from the date of delivery or collection. If your order arrives damaged, please notify us within 48 hours of delivery and provide photographs of the product and packaging.',
      },
      {
        type: 'list',
        items: [
          'Due to the nature of our products, Ecoffee Cup® South Africa does not generally accept returns or offer refunds where you have changed your mind after purchase.',
          'Any return requests for change-of-mind purchases will be considered at our sole discretion and must be approved in writing before products are returned.',
          'Approved change-of-mind returns may be subject to a reasonable handling or restocking fee where permitted by law.',
        ],
      },
      { type: 'h3', text: 'Finalisation of exchange / return' },
      {
        type: 'p',
        text: 'The exchange or return process will be finalised or confirmed after a physical assessment by the Ecoffee Cup® South Africa manager, who will ensure the following:',
      },
      {
        type: 'list',
        items: [
          'The packaging is intact and in 100% condition.',
          'The product has not been used.',
          'The product is in 100% condition.',
          'Includes all accessories, manuals and components.',
        ],
      },
      { type: 'h3', text: 'Handling of damaged or used products' },
      {
        type: 'p',
        text: 'If the packaging is not in 100% condition, the product has been used, or there is damage to the product, Ecoffee Cup® South Africa reserves the right to impose a handling fee or decline the exchange.',
      },
      { type: 'h3', text: 'Delivery charges' },
      {
        type: 'p',
        text: 'Customers will be responsible for delivery charges back to Ecoffee Cup® South Africa if the return is not due to a manufacturing fault or damage that occurred during transportation.',
      },
      { type: 'h3', text: 'Requirements for return or exchange' },
      {
        type: 'p',
        text: 'When requesting a return or exchange, customers must provide the following information to Ecoffee Cup® South Africa for further advice.',
      },
      {
        type: 'p',
        text: 'Reason for exchange: customers should clearly state the reason for the exchange. If the product has been unopened and the sealing tape has not been removed, a picture of the product must be provided.',
      },
      {
        type: 'p',
        text: 'If the product has been opened, the following is required:',
      },
      {
        type: 'list',
        items: [
          'Pictures of the product without packaging — from all four sides.',
          'Pictures of the inside of the product — clear pictures of the inside.',
          'Pictures of the repackaged product — from all four sides.',
          'Usage confirmation — indicate whether the product has been used.',
        ],
      },
      {
        type: 'p',
        text: 'Exchanges will only be considered if the original packaging is presented in the same condition as at delivery or collection, the product has not been used, the product is in 100% condition with no scratches, dents, stains or other damage, all accessories and components are included, and the item remains a current product.',
      },
      {
        type: 'note',
        text: 'If any of these requirements are not met, Ecoffee Cup® South Africa reserves the right to decline the exchange or apply a handling fee of 30% or higher, depending on the condition of the returned item.',
      },
      {
        type: 'p',
        text: 'Returns will only be accepted once authorised by Ecoffee Cup® South Africa. Where appropriate, Ecoffee Cup® South Africa may offer a replacement product instead of a refund.',
      },
    ],
  },
  {
    id: 'refunds',
    title: 'Refunds and exchanges',
    blocks: [
      {
        type: 'list',
        items: [
          'Approved refunds will be processed to the original payment method used for the purchase.',
          'Cash refunds will not be given, and any monies due will be paid back into the user’s bank account.',
          'All exchanges for faulty goods will be handled in line with the CPA. Ecoffee Cup® South Africa will inspect the faulty item and prepare a report.',
        ],
      },
      {
        type: 'p',
        text: 'If the product has not been used in accordance with the care and use instructions, this will not be classified as a product fault. Any assessment, replacement or repair decision will be subject to customer approval where applicable. If the product is confirmed to be faulty due to a material defect, the matter will be handled in accordance with the CPA and any applicable warranty terms and conditions.',
      },
      {
        type: 'list',
        items: [
          'Refunds are completed within 7 working days from the date of written agreement with Ecoffee Cup® South Africa.',
          'A handling fee may be applied to returns for unsatisfactory purchases.',
        ],
      },
      {
        type: 'p',
        text: 'Approved change-of-mind returns may be subject to a reasonable handling or restocking fee where permitted by law. Please quote your order number in the subject line of all correspondence.',
      },
    ],
  },
  {
    id: 'warranty',
    title: 'Warranty claims',
    blocks: [
      {
        type: 'p',
        text: 'Certain products may be covered by a manufacturer’s warranty. To make a claim, email sales@ecoffeecup.co.za with your order number in the subject line.',
      },
      {
        type: 'note',
        text: 'Please note that products must not be returned without prior written authorisation from Ecoffee Cup® South Africa.',
      },
    ],
  },
  {
    id: 'contact',
    title: 'Contact',
    blocks: [
      {
        type: 'table',
        head: ['Office', 'Address', 'Phone'],
        rows: [
          [
            'Johannesburg',
            'Now Media Centre, Illovo Boulevard, Upper Basement Office, 32 Fricker Road, Illovo, Sandton, 2196',
            '011 402 0062',
          ],
          [
            'Cape Town',
            'Unit 4 Coffee Depot, 47 Morningside Street, Ndabeni, Cape Town',
            '021 427 9185',
          ],
        ],
      },
      {
        type: 'p',
        text: 'Email: sales@ecoffeecup.co.za',
      },
    ],
  },
]
